import { ApolloError, ServerError, ServerParseError } from "@apollo/client";
import { Environment, EnvironmentEnum } from "@lyfegen-platform/graphql-schema/models";
import { GraphQLError } from "graphql";
import { atom } from "recoil";
import { SENTRY_ENV_NOT_SET } from "../config";
import { GetCurrentOrganization_CurrentOrganization_currencyCodes } from "../containers/layout/types/GetCurrentOrganization";
import { AgreementRunTrigger, EncapsulatedFeatureConfiguration, Mutation, Query } from "../models/generated/graphql";
import { SnackBarMessageType } from "../models/snackbarMessageType";
import { localStorageEffect } from "../utils/store";

export type UserOrg = {
	id: string | null;
	name: string | null;
	isExternalExposureAllowed?: boolean;
	currencyCodes: Array<GetCurrentOrganization_CurrentOrganization_currencyCodes>;
	agreementTagNames: Array<string>;
	encapsulatedFeatures: Array<EncapsulatedFeatureConfiguration>;
	downloadPAFeatureEnabled?: boolean;
	validityCoverageEnabled?: boolean;
	libraryGPTFeatureEnabled?: boolean;
	libraryNavlinPricesEnabled?: boolean;
	simulatorProductInputEnabled?: boolean;
	simulatorBreakdownEnabled?: boolean;
	simulatorWasteReportingEnabled?: boolean;
	simulatorPVThresholdsEnabled?: boolean;
	simulatorModelValidityEnabled?: boolean;
	documentSftpSavePath?: string;
	agreementRunTriggers: Array<AgreementRunTrigger>;
};

type BottomDrawerState = {
	key: "bottomDrawer";
	default: null | JSX.Element;
};

type BottomFixedDrawerState = {
	key: "bottomFixedDrawer";
	default: null | JSX.Element;
};

type ToastMessageState = {
	key: "toastMessage";
	default: { message: null | string };
};

export type BreadcrumbProps = {
	fromProps: true;
	links: Array<{
		label: string | number;
		link: string | undefined;
	}>;
	disableSticky?: boolean;
};

type ErrorPageState = {
	key: "errorPage";
	default: null | JSX.Element;
};

const initialErrorPageState: ErrorPageState = {
	key: "errorPage",
	default: null,
};

type BreadCrumbsState = {
	key: "breadcrumbs";
	default: null | BreadcrumbProps;
};

const initialBottomDrawerState: BottomDrawerState = {
	key: "bottomDrawer",
	default: null,
};

const initialBottomFixedDrawerState: BottomFixedDrawerState = {
	key: "bottomFixedDrawer",
	default: null,
};

const initialToastMessageState: ToastMessageState = {
	key: "toastMessage",
	default: { message: null },
};

const initialBreadCrumbsState: BreadCrumbsState = {
	key: "breadcrumbs",
	default: null,
};

type UserOrganization = {
	key: "userOrganization";
	default: UserOrg;
};

const initialValueUserOrganization: UserOrganization = {
	key: "userOrganization",
	default: {
		id: null,
		name: null,
		isExternalExposureAllowed: undefined,
		currencyCodes: [],
		agreementTagNames: [],
		encapsulatedFeatures: [],
		downloadPAFeatureEnabled: false,
		validityCoverageEnabled: false,
		libraryGPTFeatureEnabled: false,
		libraryNavlinPricesEnabled: false,
		agreementRunTriggers: [],
	},
};

type OpenQuest = {
	key: "openQuest";
	default: {
		questId: string;
	};
};

type OpenDataQuests = {
	key: "openDataQuests";
	default: {
		layers: { [key: string]: number[] };
	};
};

type AppError = {
	key: "error";
	default: {
		isError: boolean;
		message?: string | null;
		error?: ApolloError | Error | string | null | readonly GraphQLError[] | ServerError | ServerParseError | undefined;
		action?: Array<JSX.Element> | null;
		operation?: keyof Mutation | keyof Query;
		variables?: object | null;
	};
};

const BannerState: AppError = {
	key: "error",
	default: {
		isError: false,
	},
};

const NavlinUrlState = {
	key: "navlinUrl",
	default: "https://data.navlin.com/alspc/#!/detailed",
};

type ToggleMoreType = {
	key: "menu";
	default: boolean;
};

type EnvironmentState = {
	key: "environmentState";
	default: Environment | typeof SENTRY_ENV_NOT_SET;
};

const ToggleMore: ToggleMoreType = {
	key: "menu",
	default: false,
};

type SnackBar = {
	key: "warning";
	default: {
		isOpen: boolean;
		message: string | JSX.Element;
		type?: SnackBarMessageType;
		action?: JSX.Element | null;
		duration?: number | null;
	};
};

const SnackBarState: SnackBar = {
	key: "warning",
	default: {
		isOpen: false,
		message: "",
	},
};

type NotificationsInfoState = {
	key: "notificationInfo";
	default: boolean;
};

export type OrganizationCaselessAgreementsStatus = {
	key: "caselessAgreementStatus";
	default: {
		hasOnlyPatientAgreements: boolean;
		hasOnlyCaselessAgreements: boolean;
	};
};

const notificationInfo: NotificationsInfoState = {
	key: "notificationInfo",
	default: false,
};

const openQuestState: OpenQuest = {
	key: "openQuest",
	default: {
		questId: "",
	},
};

const openDataQuestsState: OpenDataQuests = {
	key: "openDataQuests",
	default: {
		layers: {},
	},
};

const organizationCaselessAgreementsState: OrganizationCaselessAgreementsStatus = {
	key: "caselessAgreementStatus",
	default: {
		hasOnlyCaselessAgreements: false,
		hasOnlyPatientAgreements: true,
	},
};

const environmentRecoilState: EnvironmentState = {
	key: "environmentState",
	default: EnvironmentEnum.Dev,
};

type PositionState = {
	key: "adjustmentWindowPosition";
	default: { x: number; y: number };
	effects_UNSTABLE: any;
};

const initialAdjustmentWindowPosition: PositionState = {
	key: "adjustmentWindowPosition",
	default: { x: 0, y: 0 },
	effects_UNSTABLE: [localStorageEffect("adjustmentWindowPosition")],
};

export const userOrganization = atom(initialValueUserOrganization);
export const bottomDrawerState = atom(initialBottomDrawerState);
export const bottomFixedDrawerState = atom(initialBottomFixedDrawerState);
export const breadCrumbsState = atom(initialBreadCrumbsState);
export const errorPageState = atom(initialErrorPageState);
export const bannerState = atom(BannerState);
export const navlinUrlState = atom(NavlinUrlState);
export const toggleMenu = atom(ToggleMore);
export const snackbarState = atom(SnackBarState);
export const notificationInfoState = atom(notificationInfo);
export const questState = atom(openQuestState);
export const dataQuestsState = atom(openDataQuestsState);
export const organizationAgreementState = atom(organizationCaselessAgreementsState);
export const environmentState = atom(environmentRecoilState);
export const adjustmentWindowPosition = atom(initialAdjustmentWindowPosition);
export const toastMessage = atom(initialToastMessageState);
